import {
  CometChatCalls,
  type CallSession,
  type SessionSettings,
} from '@cometchat/calls-sdk-javascript';
import { CometChat } from '@cometchat/chat-sdk-javascript';
import {
  CometChatMessageEvents,
  CometChatUIKitLoginListener,
} from '@cometchat/chat-uikit-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { COMETCHAT_CONSTANTS } from './AppConstants';
import ChatDemo from './ChatDemo';
import JoinSession from './JoinSession';
import KeyboardShortcuts from './components/KeyboardShortcuts';
import WaitingForParticipants from './components/WaitingForParticipants';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { getRandomMeetingId } from './utils/common';
import { composeSessionSettings as composeSettings } from './utils/session-settings';
import { MEETING_GROUP_TAG, meetingGuid } from './group-tags';
import { playSound } from './utils/sound';
import { callSettings, useCalls } from './calls/calls-context';

// LogRocket tracking helper (defined in index.html)
declare global {
  interface Window {
    trackCallSession?: (
      sessionId: string,
      userId: string,
      userName: string,
    ) => void;
  }
}

/**
 * Whether a prepared session was frozen with the lobby choices in
 * `settings`. Mic / camera are left out: the lobby forwards those to the
 * prepared session as they change. Companion mode only exists at join, so
 * choosing it never matches a prepare.
 */
function hasLobbySettings(
  session: CallSession,
  settings: SessionSettings,
): boolean {
  const frozen = session.settings;
  // Not in the web SessionSettings type, but the SDK applies it (the lobby
  // passes it the same way).
  const isCompanion = (s: SessionSettings) =>
    !!(s as { enableCompanionMode?: boolean }).enableCompanionMode;
  return (
    frozen.title === settings.title &&
    frozen.audioInputDeviceId === settings.audioInputDeviceId &&
    frozen.audioOutputDeviceId === settings.audioOutputDeviceId &&
    frozen.videoInputDeviceId === settings.videoInputDeviceId &&
    isCompanion(frozen) === isCompanion(settings)
  );
}

interface HomeProps {
  onLogout: () => void;
  /**
   * Fired when a call session starts or ends. The nav shell hides while a call
   * is running -- Home goes full-viewport, and a tab bar over live video both
   * covers content and invites a mid-call navigation away.
   */
  onSessionActiveChange?: (active: boolean) => void;
}

function Home({ onLogout, onSessionActiveChange }: HomeProps) {
  const [isStarted, setIsStarted] = useState(false);

  useEffect(() => {
    onSessionActiveChange?.(isStarted);
  }, [isStarted, onSessionActiveChange]);
  const [sessionId, setSessionId] = useState('');
  const [meetingName, setMeetingName] = useState('');
  const { joinRequest, takeJoinRequest } = useCalls();
  // The live session is a 1:1 ringing call rather than a meeting: no "waiting
  // for others / copy invite link" prompt, and the lobby is reset afterwards.
  const ringCallRef = useRef(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const playSoundOnNewMessage = useRef(true);

  // Group management state
  const [currentGroup, setCurrentGroup] = useState<CometChat.Group | null>(
    null,
  );
  // Waiting UI state
  const [showWaitingUI, setShowWaitingUI] = useState(false);
  const waitUIOpenedRef = useRef(false);
  // Unread message count state
  const [, setUnreadCount] = useState(0);

  const { showShortcuts, setShowShortcuts } = useKeyboardShortcuts(isStarted);

  const containerRef = useRef<HTMLDivElement>(null);
  // Call token for the current session id — generated once for the direct
  // joinSession() fallback (connect() mints its own from the session id).
  const callTokenRef = useRef<string | null>(null);
  // Bumped when the user backs out of the lobby preview. A connect() still in
  // flight at that moment resolves into a session nobody wants; it checks this
  // and leaves instead of being kept.
  const previewCancelRef = useRef(0);
  // Set once "Join Now" is pressed, until the lobby shows again: a prepare
  // the lobby asks for meanwhile (a device change while the join is under
  // way) must not start a second session next to the one being joined.
  const joiningRef = useRef(false);

  useEffect(() => {
    if (!isStarted) {
      joiningRef.current = false;
    }
  }, [isStarted]);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isChatOpen) {
      playSoundOnNewMessage.current = false;
    } else {
      playSoundOnNewMessage.current = true;
    }
  }, [isChatOpen]);

  // Group management functions
  const createOrJoinGroup = useCallback(
    async (rawSessionId: string, meetingName: string) => {
      const sessionId = meetingGuid(rawSessionId);
      try {
        if (!CometChat.isInitialized()) {
          console.error(
            'CometChat SDK not available. Make sure UIKit is initialized.',
          );
          return;
        }

        try {
          setMeetingName(meetingName);
          const group = await CometChat.getGroup(sessionId);
          // Check if current user has joined the group
          if (!group.getHasJoined()) {
            const joinedGroup = await CometChat.joinGroup(
              sessionId,
              group.getType() as CometChat.GroupType,
              '',
            );
            setCurrentGroup(joinedGroup);
          } else {
            setCurrentGroup(group);
          }
        } catch (error: unknown) {
          // Group doesn't exist, create it
          let code: string | undefined;
          if (typeof error === 'object' && error !== null && 'code' in error) {
            code = (error as { code?: string }).code;
          }
          if (code === 'ERR_GUID_NOT_FOUND') {
            const group = new CometChat.Group(
              sessionId,
              meetingName,
              CometChat.GROUP_TYPE.PUBLIC,
              undefined,
            );
            // Keeps it out of the Groups tab, which lists ringable groups only.
            group.setTags([MEETING_GROUP_TAG]);
            const createdGroup = await CometChat.createGroup(group);
            setCurrentGroup(createdGroup);
          } else if (code !== 'ERR_ALREADY_JOINED') {
            console.error('Error getting group:', error);
          }
        }
      } catch (error) {
        console.error('Error in group management:', error);
      }
    },
    [],
  );

  // Confirmation for the invite-link copy: an M3 snackbar rather than the
  // browser alert() it used to be, which ignored the theme and blocked the page.
  const [snackbar, setSnackbar] = useState<string | null>(null);
  const snackbarTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showSnackbar = useCallback((text: string) => {
    if (snackbarTimerRef.current) clearTimeout(snackbarTimerRef.current);
    setSnackbar(text);
    snackbarTimerRef.current = setTimeout(() => setSnackbar(null), 3000);
  }, []);

  const handleCopyInviteLink = useCallback(async (): Promise<boolean> => {
    const inviteLink = `https://calls.${COMETCHAT_CONSTANTS.IS_STAGING ? 'cometchat-staging.com' : 'cometchat.io'}/?sessionId=${sessionId}&meetingName=${encodeURIComponent(meetingName)}`;
    try {
      await navigator.clipboard.writeText(inviteLink);
      showSnackbar('Invite link copied');
      return true;
    } catch (err) {
      console.error('Failed to copy invite link:', err);
      showSnackbar("Couldn't copy the invite link");
      return false;
    }
  }, [meetingName, sessionId, showSnackbar]);

  useEffect(() => {
    // Add event listener for chat button
    const chatButtonUnsub = CometChatCalls.addEventListener(
      'onChatButtonClicked',
      () => {
        setIsChatOpen(prev => {
          const newChatOpen = !prev;
          if (newChatOpen) {
            setUnreadCount(0);
            CometChatCalls.setChatButtonUnreadCount(0);
          }
          return newChatOpen;
        });
      },
    );
    const participantListOpenUnSub = CometChatCalls.addEventListener(
      'onParticipantListButtonClicked',
      () => {
        setIsChatOpen(false);
      },
    );

    const participantListChangedUnsub = CometChatCalls.addEventListener(
      'onParticipantListChanged',
      participants => {
        console.log(
          'Participant list changed:',
          participants.length,
          'participants',
        );

        if (participants.length >= 1) {
          setShowWaitingUI(false);
        } else {
          if (!waitUIOpenedRef.current) {
            setShowWaitingUI(true);
            waitUIOpenedRef.current = true;
          }
        }
      },
    );

    const sessionJoinedUnsub = CometChatCalls.addEventListener(
      'onSessionJoined',
      () => {
        console.log(
          'Session joined - setting up group. Current sessionId:',
          sessionId,
        );
        console.timeEnd('JoinedSessionIn');
        const currentSessionId = sessionId || `session-${Date.now()}`;
        createOrJoinGroup(currentSessionId, 'CometChat Team Meeting');

        const loggedInUser = CometChatCalls.getLoggedInUser();
        if (window.trackCallSession && loggedInUser) {
          window.trackCallSession(
            currentSessionId,
            loggedInUser.uid || 'unknown',
            loggedInUser.name || 'Unknown User',
          );
        }

        if (!waitUIOpenedRef.current) {
          setShowWaitingUI(true);
          waitUIOpenedRef.current = true;
        }
      },
    );

    const onShareInviteButtonClicked = CometChatCalls.addEventListener(
      'onShareInviteButtonClicked',
      () => {
        handleCopyInviteLink();
      },
    );

    return () => {
      chatButtonUnsub();
      sessionJoinedUnsub();
      participantListOpenUnSub();
      participantListChangedUnsub();
      onShareInviteButtonClicked();
    };
  }, [sessionId, createOrJoinGroup, handleCopyInviteLink]);

  useEffect(() => {
    const joinCallUnsub = CometChatCalls.addEventListener(
      'onSessionJoined',
      () => {
        playSound('join-call');
      },
    );

    CometChatCalls.addEventListener('onParticipantJoined', () => {
      playSound('join-call');
    });

    CometChatCalls.addEventListener('onSessionLeft', () => {
      playSound('leave-call');
    });

    CometChatCalls.addEventListener('onParticipantLeft', () => {
      playSound('leave-call');
    });

    CometChatCalls.addEventListener('onParticipantHandRaised', () => {
      playSound('group-chat-received');
    });

    CometChatCalls.addEventListener('onParticipantStartedScreenShare', () => {
      playSound('group-chat-received');
    });

    return () => {
      joinCallUnsub();
    };
  }, []);

  useEffect(() => {
    const unsub = CometChatCalls.addEventListener('onConnectionClosed', () => {
      // Fires when a call ends and when a prepared session is torn down
      // (timed out, fatal error, cancel). Either way the next call
      // starts fresh with a new token.
      callTokenRef.current = null;
      if (ringCallRef.current) {
        // A 1:1 call ended: don't leave its session id in the lobby.
        ringCallRef.current = false;
        setSessionId('');
        setMeetingName('');
      }
      setIsStarted(false);
      setShowWaitingUI(false);
      setUnreadCount(0);
      CometChatCalls.setChatButtonUnreadCount(0);
      setIsChatOpen(false);
    });

    return () => {
      unsub();
    };
  }, []);

  const isMessageOfCurrentChat = useCallback(
    (message: CometChat.BaseMessage) => {
      return (
        currentGroup &&
        message.getReceiverId() == currentGroup.getGuid() &&
        message.getSender().getUid() !=
          CometChatUIKitLoginListener.getLoggedInUser()?.getUid()
      );
    },
    [currentGroup],
  );

  useEffect(() => {
    type Subscription = { unsubscribe: () => void } | undefined;
    let textMessageSubscription: Subscription;
    let mediaMessageSubscription: Subscription;

    if (isStarted) {
      textMessageSubscription =
        CometChatMessageEvents.onTextMessageReceived.subscribe(
          (message: CometChat.TextMessage) => {
            if (isMessageOfCurrentChat(message) && !isChatOpen) {
              if (playSoundOnNewMessage.current) {
                playSound('group-chat-received');
                playSoundOnNewMessage.current = false;
              }
              setUnreadCount(prev => {
                const newCount = prev + 1;
                CometChatCalls.setChatButtonUnreadCount(newCount);
                return newCount;
              });
            }
          },
        );
      mediaMessageSubscription =
        CometChatMessageEvents.onMediaMessageReceived.subscribe(
          (message: CometChat.MediaMessage) => {
            if (isMessageOfCurrentChat(message) && !isChatOpen) {
              if (playSoundOnNewMessage.current) {
                playSound('group-chat-received');
                playSoundOnNewMessage.current = false;
              }
              setUnreadCount(prev => {
                const newCount = prev + 1;
                CometChatCalls.setChatButtonUnreadCount(newCount);
                return newCount;
              });
            }
          },
        );
    }
    return () => {
      textMessageSubscription?.unsubscribe();
      mediaMessageSubscription?.unsubscribe();
    };
  }, [isStarted, isMessageOfCurrentChat, isChatOpen]);

  // A new session id needs a new call token.
  useEffect(() => {
    callTokenRef.current = null;
  }, [sessionId]);

  const getCallToken = useCallback(async () => {
    if (!callTokenRef.current) {
      const { token } = await CometChatCalls.generateToken(
        sessionId || getRandomMeetingId(),
      );
      callTokenRef.current = token;
    }
    return callTokenRef.current;
  }, [sessionId]);

  // The full settings the call is configured with: what the lobby chose plus
  // the sample app's own defaults / overrides.
  const composeSessionSettings = useCallback(
    (lobbySettings: SessionSettings) => composeSettings(lobbySettings),
    [],
  );

  // The session the lobby prepared for `id` with connect(), while it is
  // still preparing or prepared (not yet joined). The SDK has one session
  // object, so this also checks it is this meeting's and not, say, a ringing
  // 1:1 call the caller prepared from the Users tab.
  const getLobbySession = (id: string): CallSession | null => {
    const session = CometChatCalls.getActiveSession();
    const state = session?.getSessionState();
    return session &&
      session.sessionId === id &&
      (state === 'preparing' || state === 'prepared')
      ? session
      : null;
  };

  // Called while the user is in the lobby: verifies the token and joins the
  // conference in "prepared" state (no camera/mic, invisible to others) so
  // that "Join Now" is near-instant.
  const prepareCall = useCallback(
    async (lobbySettings: SessionSettings) => {
      if (!sessionId || joiningRef.current) {
        return;
      }
      const settings = composeSessionSettings(lobbySettings);
      // Settings are frozen at prepare, and connect() for a session that is
      // already preparing / prepared hands back that one unchanged. So the
      // lobby calls this again when a setting changes (title, devices), and
      // the current prepare is dropped first unless it already matches.
      // Read before the first await, so a Back pressed during it still counts.
      const cancelMark = previewCancelRef.current;
      const current = getLobbySession(sessionId);
      if (current && hasLobbySettings(current, settings)) {
        return;
      }
      await current?.leave();
      if (previewCancelRef.current !== cancelMark || joiningRef.current) {
        // Back or "Join Now" was pressed while the old prepare was dropped.
        return;
      }
      try {
        await CometChatCalls.connect(sessionId, settings);
        if (previewCancelRef.current !== cancelMark) {
          // The user went back while this was connecting.
          await getLobbySession(sessionId)?.leave();
        }
      } catch (error) {
        // Not fatal: joinCall() below falls back to a direct join.
        console.warn('connect failed:', error);
      }
    },
    [sessionId, composeSessionSettings],
  );

  // The user left the lobby preview with Back: release the prepared session
  // (cancelling its connect() if that is still in flight).
  const cancelPreview = useCallback(() => {
    previewCancelRef.current += 1;
    getLobbySession(sessionId)?.leave();
  }, [sessionId]);

  // Called on "Join Now". If the session is prepared (or still preparing —
  // join() waits for it) with the settings the lobby shows now, this is the
  // actual join through the session object. Otherwise it is a plain direct
  // join: nothing was prepared, the prepare is out of date (a change inside
  // the debounce), or companion mode was chosen, which is decided at join.
  const joinCall = useCallback(
    async (lobbySettings: SessionSettings) => {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      joiningRef.current = true;
      const sessionIdToJoin = sessionId || getRandomMeetingId();
      window.history.replaceState(
        {},
        document.title,
        `/${sessionIdToJoin}?meetingName=${lobbySettings.title || 'CometChat Team Meeting'}`,
      );

      const settings = composeSessionSettings(lobbySettings);
      const lobbySession = getLobbySession(sessionIdToJoin);
      if (lobbySession && hasLobbySettings(lobbySession, settings)) {
        lobbySession.join(container);
      } else {
        try {
          await lobbySession?.leave();
          const token = await getCallToken();
          CometChatCalls.joinSession(token, settings, container);
        } catch (error) {
          console.error('Joining the meeting failed:', error);
          joiningRef.current = false;
          return;
        }
      }
      setIsStarted(true);

      // Create or join group when call starts
      if (sessionIdToJoin) {
        createOrJoinGroup(
          sessionIdToJoin,
          meetingName || 'CometChat Team Meeting',
        );
      }
    },
    [
      sessionId,
      getCallToken,
      composeSessionSettings,
      createOrJoinGroup,
      meetingName,
    ],
  );

  // A ringing call was answered (calls/CallProvider): join it here, where the
  // call container lives. The caller takes over the session initiateCall
  // prepared; the callee joins directly, as meetings do.
  useEffect(() => {
    if (!joinRequest || !containerRef.current) return;
    const request = takeJoinRequest();
    if (!request) return;
    ringCallRef.current = true;
    setSessionId(request.sessionId);
    setMeetingName(request.title);
    const container = containerRef.current;
    (async () => {
      // Let the session id reach the onSessionJoined handler (in-call chat
      // group) before the join can fire it.
      await new Promise(resolve => setTimeout(resolve, 0));
      if (request.session) {
        request.session.join(container);
      } else {
        const { token } = await CometChatCalls.generateToken(request.sessionId);
        CometChatCalls.joinSession(
          token,
          callSettings(request.type, request.title, request.isPeerCall),
          container,
        );
      }
      setIsStarted(true);
      createOrJoinGroup(request.sessionId, request.title);
    })().catch(error => {
      console.error('Joining the call failed:', error);
      ringCallRef.current = false;
    });
  }, [joinRequest, takeJoinRequest, createOrJoinGroup]);

  return (
    <>
      {!isStarted && (
        <JoinSession
          sessionId={sessionId}
          initialMeetingName={meetingName}
          onSessionIdChange={setSessionId}
          onPrepareSession={sessionSettings => prepareCall(sessionSettings)}
          onStartSession={sessionSettings => {
            joinCall(sessionSettings);
          }}
          onCancelPreview={cancelPreview}
          onLogout={onLogout}
        />
      )}
      <div
        className="call-container__wrapper"
        style={{
          display: isStarted ? 'block' : 'none',
          position: 'relative',
        }}>
        <div
          ref={containerRef}
          className={`call-container-main__wrapper ${isChatOpen ? 'chat-open' : ''}`}
          style={{
            width: isMobile
              ? '100vw'
              : isChatOpen
                ? 'calc(100vw - 360px)'
                : '100vw',
          }}
        />
        {showWaitingUI && sessionId && !ringCallRef.current && (
          <WaitingForParticipants
            onCopyInviteLink={handleCopyInviteLink}
            onClose={() => {
              setShowWaitingUI(false);
            }}
          />
        )}
        <div
          className={`chat-demo-container ${isChatOpen ? 'chat-open' : ''}`}
          style={{
            transform: isMobile
              ? undefined
              : isChatOpen
                ? 'initial'
                : 'translateX(100%)',
            display: 'flex',
          }}>
          {currentGroup && (
            <ChatDemo
              onClose={() => {
                setIsChatOpen(false);
                setUnreadCount(0);
                CometChatCalls.setChatButtonUnreadCount(0);
              }}
              group={currentGroup}
            />
          )}
        </div>
      </div>
      {snackbar && (
        <div className="md-snackbar" role="status" aria-live="polite">
          {snackbar}
        </div>
      )}
      <KeyboardShortcuts
        open={showShortcuts}
        onClose={() => setShowShortcuts(false)}
      />
    </>
  );
}

export default Home;
